import { type Option, Select } from "@backstage/ui";

import { CODE_FONTS } from "../../constants";
import styles from "./CodeFontPicker.module.css";

/**
 * Props for the {@link CodeFontPicker} component.
 *
 * @public
 */
interface CodeFontPickerProps {
    /**
     * The label of the currently selected font, matching a `label` value from {@link CODE_FONTS}.
     */
    fontLabel: string;
    /**
     * Callback invoked when the user selects a different font.
     *
     * @param next - The label of the newly selected font.
     */
    onChange: (next: string) => void;
}

/**
 * Select options derived from {@link CODE_FONTS}, using each font's label as both
 * the display text and the option value.
 */
const FONT_OPTIONS: Option[] = CODE_FONTS.map(f => ({
    value: f.label,
    label: f.label,
}));

/**
 * A dropdown picker that lets the user choose a monospace code font.
 *
 * Renders a labeled {@link @backstage/ui#Select} populated with all available
 * code fonts defined in {@link CODE_FONTS}. The selected value is controlled via
 * `fontLabel`, and changes are reported via `onChange` with the label string of
 * the newly chosen font.
 *
 * @param props - {@link CodeFontPickerProps}
 *
 * @public
 */
export const CodeFontPicker = ({
    fontLabel,
    onChange,
}: CodeFontPickerProps) => {
    return (
        <div className={styles.row}>
            <Select
                label="Font"
                options={FONT_OPTIONS}
                value={fontLabel}
                onChange={key => onChange(String(key))}
            />
        </div>
    );
};
